/**
 * 权限服务
 * 从后端加载用户权限
 */

import axios from 'axios';
import api from '../../api';
import { usePermissionStore } from '../../../store/permissionStore';
import { logger } from '../../../utils/logger';

const TOKEN_KEY = 'token';

function clearPermissionsOnFailure(): void {
  usePermissionStore.getState().clearPermissions();
}

/**
 * 权限API响应
 */
interface PermissionResponse {
  code: number;
  data: {
    permissions: string[];
    role: string;
    roles?: string[];
  };
  msg?: string;
  /** 与后端 `Result.message` 对齐 */
  message?: string;
}

/**
 * 加载用户权限
 */
export const loadUserPermissions = async (): Promise<boolean> => {
  try {
    if (typeof window !== 'undefined' && !localStorage.getItem(TOKEN_KEY)) {
      logger.debug('loadUserPermissions: skip（无 token）');
      clearPermissionsOnFailure();
      return false;
    }

    // 从后端获取用户权限
    const response = await api.get('/auth/permissions') as PermissionResponse;
    
    if (response.code === 200 && response.data) {
      const { permissions, role, roles } = response.data;
      
      // 保存到权限Store
      usePermissionStore.getState().setPermissions(permissions || []);
      usePermissionStore.getState().setRole(role || '');
      usePermissionStore.getState().setRoles(
        roles && roles.length > 0 ? roles : (role ? [role] : [])
      );
      
      logger.info('用户权限加载成功:', { permissionCount: permissions?.length, role, roles });
      return true;
    } else {
      logger.error('加载用户权限失败:', response.message ?? response.msg);
      clearPermissionsOnFailure();
      return false;
    }
  } catch (error) {
    if (error && typeof error === 'object' && '__CANCELLED__' in error) {
      return false;
    }
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // 未登录或会话失效；响应拦截器可能已 clearPermissions，此处勿再 setPermissions([])（否则会 loaded=true）
      clearPermissionsOnFailure();
      logger.debug('加载用户权限跳过（401，未登录或会话无效）');
      return false;
    }
    logger.error('加载用户权限异常:', error);
    clearPermissionsOnFailure();
    return false;
  }
};

/**
 * 清除用户权限
 */
export const clearUserPermissions = () => {
  usePermissionStore.getState().clearPermissions();
  logger.info('用户权限已清除');
};

/**
 * 检查权限是否已加载
 */
export const isPermissionLoaded = (): boolean => {
  return usePermissionStore.getState().loaded;
};

/**
 * 获取当前用户权限列表
 */
export const getCurrentPermissions = (): string[] => {
  return usePermissionStore.getState().permissions;
};

/**
 * 获取当前用户角色
 */
export const getCurrentRole = (): string => {
  return usePermissionStore.getState().role;
};
